import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BLOG_CATEGORIES, slugify, type BlogCategory } from "@/lib/blog";
import { ArrowLeft, Upload, X, Sparkles } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { z } from "zod";

const ALLOWED_IMAGE_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const PostSchema = z.object({
  title: z.string().trim().min(3, "Titel mind. 3 Zeichen").max(200),
  slug: z.string().trim().regex(/^[a-z0-9-]+$/, "Nur Kleinbuchstaben, Zahlen und Bindestriche").min(3).max(120),
  excerpt: z.string().trim().max(300).optional().or(z.literal("")),
  content: z.string().min(1, "Inhalt fehlt").max(200000),
  meta_title: z.string().trim().max(70, "Meta-Titel max. 70 Zeichen").optional().or(z.literal("")),
  meta_description: z.string().trim().max(160, "Meta-Description max. 160 Zeichen").optional().or(z.literal("")),
  meta_keywords: z.string().trim().max(300).optional().or(z.literal("")),
  cover_image_alt: z.string().trim().max(200).optional().or(z.literal("")),
});

const AdminBlogEdit = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [coverAlt, setCoverAlt] = useState("");
  const [category, setCategory] = useState<BlogCategory>("mikrobiom");
  const [published, setPublished] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [showToc, setShowToc] = useState(true);
  const [showCta, setShowCta] = useState(true);
  const [showRelated, setShowRelated] = useState(true);
  const fileRef = useRef<HTMLInputElement>(null);

  // Lazy-load editor (heavy)
  const [Editor, setEditor] = useState<React.ComponentType<{ value: string; onChange: (v: string) => void }> | null>(null);
  useEffect(() => {
    import("@/components/admin/RichTextEditor").then((m) => setEditor(() => m.RichTextEditor));
  }, []);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error || !data) {
        toast.error("Beitrag nicht gefunden");
        navigate("/admin/blog");
        return;
      }
      setTitle(data.title);
      setSlug(data.slug);
      setExcerpt(data.excerpt ?? "");
      setContent(data.content ?? "");
      setCoverUrl(data.cover_image_url);
      setCoverAlt((data as any).cover_image_alt ?? "");
      setCategory(data.category as BlogCategory);
      setPublished(data.published);
      setMetaTitle((data as any).meta_title ?? "");
      setMetaDescription((data as any).meta_description ?? "");
      setMetaKeywords((data as any).meta_keywords ?? "");
      setShowToc((data as any).show_toc ?? true);
      setShowCta((data as any).show_cta ?? true);
      setShowRelated((data as any).show_related ?? true);
      setSlugTouched(true);
      setLoading(false);
    })();
  }, [id, isNew, navigate]);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(title));
  }, [title, slugTouched]);

  const uploadCover = async (file: File) => {
    if (!ALLOWED_IMAGE_MIME.includes(file.type)) {
      return toast.error("Ungültiger Dateityp", { description: "Erlaubt: JPG, PNG, WebP, GIF." });
    }
    if (file.size > MAX_IMAGE_SIZE) {
      return toast.error("Datei zu groß", { description: "Maximal 5 MB." });
    }
    const ext = file.name.split(".").pop();
    const fileName = `covers/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("blog-images").upload(fileName, file, {
      contentType: file.type,
    });
    if (error) return toast.error("Upload fehlgeschlagen", { description: error.message });
    const { data } = supabase.storage.from("blog-images").getPublicUrl(fileName);
    setCoverUrl(data.publicUrl);
    toast.success("Titelbild hochgeladen");
  };

  const generateSeo = () => {
    if (!title.trim() && !excerpt.trim() && !content.trim()) {
      return toast.error("Bitte zuerst Titel oder Kurzbeschreibung ausfüllen");
    }
    const brand = "Praxis IB";
    // Meta-Titel
    const baseTitle = title.trim();
    let mt = baseTitle ? `${baseTitle} | ${brand}` : brand;
    if (mt.length > 70) mt = baseTitle.slice(0, 70 - (brand.length + 3)).trim() + ` | ${brand}`;
    if (mt.length > 70) mt = baseTitle.slice(0, 70);
    setMetaTitle(mt);

    // Meta-Description aus Excerpt oder Content
    const plain = (excerpt.trim() || content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim());
    let md = plain;
    if (md.length > 160) md = md.slice(0, 157).replace(/\s+\S*$/, "") + "…";
    setMetaDescription(md);

    // Keywords: Kategorie + relevante Begriffe (Phrasen + Einzelwörter) aus Titel & Excerpt
    const STOP = new Set([
      // Artikel/Pronomen
      "der","die","das","den","dem","des","ein","eine","einen","einem","einer","eines",
      "ich","du","er","sie","es","wir","ihr","mich","dich","sich","mir","dir","uns","euch","ihm","ihn","ihnen",
      "mein","meine","meinen","meiner","meines","dein","deine","sein","seine","ihre","ihrer","unser","unsere",
      // Hilfsverben/häufige Verben
      "ist","sind","war","waren","bin","bist","sein","wird","werde","werden","wurde","wurden","worden",
      "habe","hast","hat","haben","hatte","hatten","kann","kannst","können","könnte","muss","müssen","soll","sollen","will","wollen","mag","mögen","darf","dürfen",
      // Konjunktionen/Präpositionen
      "und","oder","aber","sondern","denn","weil","wenn","dann","als","wie","was","wer","wo","wohin","woher","warum","ob",
      "im","in","an","am","auf","mit","für","von","vom","zu","zur","zum","bei","beim","aus","über","unter","durch","nach","vor","seit","gegen","ohne","um","ab",
      // Adverbien/Füllwörter
      "nicht","nur","auch","schon","noch","sehr","mehr","ganz","etwa","etwas","viel","viele","wenig","alle","alles","jede","jeder","jedes","jedoch","doch","also","sogar","immer","oft","manchmal","heute","gestern","morgen","hier","dort","da","so","dass","damit","bevor","während","zwischen",
      // Häufige Verben/Allgemein
      "geht","macht","machen","gibt","geben","kommt","kommen","sagt","sagen","gut","besser","beste","neu","neue","neuen","alt","alte","klein","groß","große","sowie","bzw","z.b","etc","mehr","weniger",
    ]);

    const titleClean = baseTitle.replace(/[–—:|\-]/g, " ").trim();
    const excerptClean = (excerpt.trim() || plain).slice(0, 600);

    // Multi-Wort-Phrasen aus Titel (jeweils 2 zusammenhängende Wörter ohne Stoppwörter)
    const tokens = (text: string) =>
      text
        .toLowerCase()
        .replace(/[^a-zäöüß0-9\s-]/gi, " ")
        .split(/\s+/)
        .filter(Boolean);

    const titleTokens = tokens(titleClean);
    const phrases: string[] = [];
    for (let i = 0; i < titleTokens.length - 1; i++) {
      const a = titleTokens[i], b = titleTokens[i + 1];
      if (a.length > 3 && b.length > 3 && !STOP.has(a) && !STOP.has(b)) {
        phrases.push(`${a} ${b}`);
      }
    }

    // Einzelne aussagekräftige Begriffe (Substantive ≥5 Zeichen bevorzugen)
    const singles = [...titleTokens, ...tokens(excerptClean)]
      .filter((w) => w.length >= 5 && !STOP.has(w) && !/^\d+$/.test(w));
    const singleSet: string[] = [];
    const seen = new Set<string>();
    for (const w of singles) {
      if (!seen.has(w)) { seen.add(w); singleSet.push(w); }
    }

    const catLabel = BLOG_CATEGORIES.find((c) => c.value === category)?.label.toLowerCase();
    const merged: string[] = [];
    const pushUniq = (v?: string) => { if (v && !merged.includes(v)) merged.push(v); };
    pushUniq(catLabel);
    phrases.slice(0, 3).forEach(pushUniq);
    singleSet.slice(0, 8).forEach(pushUniq);

    setMetaKeywords(merged.join(", ").slice(0, 300));

    toast.success("SEO-Felder generiert – kannst du jetzt anpassen");
  };

  const save = async () => {
    const parsed = PostSchema.safeParse({ title, slug, excerpt, content, meta_title: metaTitle, meta_description: metaDescription, meta_keywords: metaKeywords, cover_image_alt: coverAlt });
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      return toast.error(first?.message ?? "Ungültige Eingabe");
    }

    setSaving(true);
    const payload = {
      title: title.trim(),
      slug: slug.trim(),
      excerpt: excerpt.trim() || null,
      content,
      cover_image_url: coverUrl,
      cover_image_alt: coverAlt.trim() || null,
      category,
      published,
      published_at: published ? new Date().toISOString() : null,
      meta_title: metaTitle.trim() || null,
      meta_description: metaDescription.trim() || null,
      meta_keywords: metaKeywords.trim() || null,
      show_toc: showToc,
      show_cta: showCta,
      show_related: showRelated,
    };

    if (isNew) {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("blog_posts")
        .insert({ ...payload, author_id: user?.id })
        .select("id")
        .single();
      setSaving(false);
      if (error) return toast.error(error.message);
      toast.success("Beitrag erstellt");
      navigate(`/admin/blog/${data.id}`);
    } else {
      const { error } = await supabase.from("blog_posts").update(payload).eq("id", id!);
      setSaving(false);
      if (error) return toast.error(error.message);
      toast.success("Gespeichert");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Lade…</div>;

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="bg-background border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/blog"><ArrowLeft className="w-4 h-4 mr-2" />Zurück</Link>
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch checked={published} onCheckedChange={setPublished} id="pub" />
              <Label htmlFor="pub" className="text-sm cursor-pointer">Online</Label>
            </div>
            <Button onClick={save} disabled={saving}>
              {saving ? "Speichere…" : "Speichern"}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
        <Card className="p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titel</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Mein neuer Beitrag" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="slug">Slug (URL)</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
              />
            </div>
            <div className="space-y-2">
              <Label>Kategorie</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as BlogCategory)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BLOG_CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="excerpt">Kurzbeschreibung (für die Übersicht)</Label>
            <Textarea
              id="excerpt"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="Kurze Zusammenfassung des Beitrags…"
            />
          </div>
        </Card>

        <Card className="p-6 space-y-3">
          <Label>Titelbild</Label>
          {coverUrl ? (
            <div className="relative rounded-lg overflow-hidden border border-border">
              <img src={coverUrl} alt="Cover" className="w-full h-64 object-cover" />
              <Button
                variant="destructive" size="sm"
                className="absolute top-2 right-2"
                onClick={() => setCoverUrl(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full h-48 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Upload className="w-8 h-8" />
              <span className="text-sm">Titelbild hochladen</span>
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadCover(f);
              e.target.value = "";
            }}
          />
          <div className="space-y-2 pt-2">
            <Label htmlFor="cover-alt">Alt-Text (Bildbeschreibung für Barrierefreiheit & SEO)</Label>
            <Input
              id="cover-alt"
              value={coverAlt}
              onChange={(e) => setCoverAlt(e.target.value)}
              maxLength={200}
              placeholder="z.B. Mikroskopaufnahme von Darmbakterien"
            />
            <p className="text-xs text-muted-foreground">
              Wird leer gelassen, wird der Beitragstitel als Alt-Text verwendet.
            </p>
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-semibold text-foreground">SEO &amp; Meta-Daten</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Optional – wenn leer, werden Titel und Kurzbeschreibung verwendet. Das Titelbild dient automatisch als Open-Graph-Bild.
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={generateSeo} className="shrink-0">
              <Sparkles className="w-4 h-4 mr-2" />
              Auto-Generieren
            </Button>
          </div>
          <div className="space-y-2">
            <Label htmlFor="meta-title">Meta-Titel ({metaTitle.length}/70)</Label>
            <Input
              id="meta-title"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              maxLength={70}
              placeholder="z.B. Mikrobiom verstehen – Praxis IB Wien"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="meta-description">Meta-Description ({metaDescription.length}/160)</Label>
            <Textarea
              id="meta-description"
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              rows={2}
              maxLength={160}
              placeholder="Kurzer Snippet-Text für Google (≤160 Zeichen)"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="meta-keywords">Keywords (Komma-getrennt)</Label>
            <Input
              id="meta-keywords"
              value={metaKeywords}
              onChange={(e) => setMetaKeywords(e.target.value)}
              maxLength={300}
              placeholder="mikrobiom, darmgesundheit, komplementärtherapie"
            />
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <div>
            <h3 className="font-semibold text-foreground">Zusätzliche Beitrags-Elemente</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Diese Bereiche werden am Beitrag automatisch eingeblendet. Hier kannst du sie pro Beitrag deaktivieren.
            </p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={showToc} onCheckedChange={(v) => setShowToc(v === true)} className="mt-0.5" />
            <span className="space-y-0.5">
              <span className="block text-sm font-medium text-foreground">Inhaltsverzeichnis anzeigen</span>
              <span className="block text-xs text-muted-foreground">Automatisch aus allen H2/H3-Überschriften im Inhalt (ab 2 Überschriften).</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={showCta} onCheckedChange={(v) => setShowCta(v === true)} className="mt-0.5" />
            <span className="space-y-0.5">
              <span className="block text-sm font-medium text-foreground">CTA-Box „Termin vereinbaren" anzeigen</span>
              <span className="block text-xs text-muted-foreground">Verlinkt zu Kontakt &amp; Leistungen am Ende des Beitrags.</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={showRelated} onCheckedChange={(v) => setShowRelated(v === true)} className="mt-0.5" />
            <span className="space-y-0.5">
              <span className="block text-sm font-medium text-foreground">Weitere Beiträge verlinken</span>
              <span className="block text-xs text-muted-foreground">Zeigt bis zu 3 thematisch passende Beiträge unter dem Artikel.</span>
            </span>
          </label>
        </Card>


        <Card className="p-6 space-y-3">
          <Label>Inhalt</Label>
          {Editor ? (
            <Editor value={content} onChange={setContent} />
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">Editor wird geladen…</div>
          )}
        </Card>
      </main>
    </div>
  );
};

export default AdminBlogEdit;
